/**
 * UI grouping cho Orders aggregate (EG-78).
 *
 * Tôn trọng @tcr-erp/shared canonical (11 OrderChannel + OrderState enum) —
 * FE thêm group mapping ở UI layer. BE EG-376 sẽ resolve group → array enum.
 */

import {
  ACTIVE_ORDER_STATES,
  B2BChannelType,
  OrderChannel,
  OrderStatusGroup,
  OrderState,
  getOrderStateText,
} from '@tcr-erp/shared';

// ============================================================
// Channel grouping — 11 enum BE → 5 group UI
// ============================================================

export type OrderChannelGroup = 'pos' | 'online' | 'livestream' | 'dealer' | 'phone' | 'other';

export const CHANNEL_GROUP_MAP: Record<OrderChannelGroup, OrderChannel[]> = {
  pos: [OrderChannel.POS],
  online: [OrderChannel.WEB, OrderChannel.APP, OrderChannel.SHOPEE],
  livestream: [OrderChannel.FACEBOOK, OrderChannel.TIKTOK],
  dealer: [OrderChannel.DEALER_PORTAL],
  phone: [OrderChannel.ZALO],
  other: [OrderChannel.OTHER, OrderChannel.BIDV, OrderChannel.BO],
};

export function resolveChannelGroup(channel: OrderChannel): OrderChannelGroup {
  for (const group of Object.keys(CHANNEL_GROUP_MAP) as OrderChannelGroup[]) {
    if (CHANNEL_GROUP_MAP[group].includes(channel)) return group;
  }
  return 'other';
}

export const CHANNEL_GROUP_LABEL: Record<OrderChannelGroup, string> = {
  pos: 'POS',
  online: 'Online',
  livestream: 'Livestream',
  dealer: 'Đại lý',
  phone: 'Điện thoại',
  other: 'Khác',
};

export const CHANNEL_GROUP_ICON: Record<OrderChannelGroup, string> = {
  pos: '🛒',
  online: '🌐',
  livestream: '📺',
  dealer: '💼',
  phone: '📞',
  other: '❓',
};

export const CHANNEL_GROUP_COLOR: Record<OrderChannelGroup, string> = {
  pos: 'cyan',
  online: 'geekblue',
  livestream: 'magenta',
  dealer: 'gold',
  phone: 'purple',
  other: 'default',
};

// ============================================================
// TCR-2127 — bộ lọc Kênh tách "B2B POS" theo kênh khách B2B (Sỉ/GT/MT)
// ============================================================

/** Nhãn ngắn của kênh B2B trên cột/bộ lọc Kênh ("B2B Sỉ POS"), khác nhãn dài ở hồ sơ khách. */
export const B2B_CHANNEL_SHORT_LABEL: Record<B2BChannelType, string> = {
  [B2BChannelType.SI]: 'Sỉ',
  [B2BChannelType.GT]: 'GT',
  [B2BChannelType.MT]: 'MT',
};

/**
 * Lựa chọn của bộ lọc Kênh (giá trị URL `channel_group`). Ngoài 6 group kênh nguồn, POS được tách
 * theo loại khách: `pos` = POS bán lẻ (khách cá nhân), `b2b_*_pos` = POS khách tổ chức theo kênh
 * B2B snapshot trên đơn (`Order.b2b_channel`), `b2b_none_pos` = khách tổ chức chưa phân kênh.
 */
export type OrderChannelFilter =
  | OrderChannelGroup
  | 'b2b_si_pos'
  | 'b2b_gt_pos'
  | 'b2b_mt_pos'
  | 'b2b_none_pos';

const B2B_POS_FILTER_CHANNEL: Record<string, B2BChannelType | 'none'> = {
  b2b_si_pos: B2BChannelType.SI,
  b2b_gt_pos: B2BChannelType.GT,
  b2b_mt_pos: B2BChannelType.MT,
  b2b_none_pos: 'none',
};

export const CHANNEL_FILTER_LABEL: Record<OrderChannelFilter, string> = {
  ...CHANNEL_GROUP_LABEL,
  b2b_si_pos: 'B2B Sỉ POS',
  b2b_gt_pos: 'B2B GT POS',
  b2b_mt_pos: 'B2B MT POS',
  b2b_none_pos: 'B2B POS (chưa phân kênh)',
};

export interface ChannelFilterParams {
  channel?: string;
  user_type?: 'personal' | 'organization';
  b2b_channel?: string;
}

/** Đổi giá trị bộ lọc Kênh ra tham số `GET /orders` + `/orders/export`. Giá trị lạ → `{}`. */
export function resolveChannelFilterParams(value: string): ChannelFilterParams {
  const b2bChannel = B2B_POS_FILTER_CHANNEL[value];
  if (b2bChannel) {
    return { channel: OrderChannel.POS, user_type: 'organization', b2b_channel: b2bChannel };
  }
  if (value === 'pos') return { channel: OrderChannel.POS, user_type: 'personal' };
  const channels = CHANNEL_GROUP_MAP[value as OrderChannelGroup];
  return channels ? { channel: channels.join(',') } : {};
}

/** Nhãn cột Kênh / file export: "POS", "B2B POS", "B2B Sỉ POS"... */
export function resolveOrderChannelLabel(order: {
  channel: OrderChannel;
  user_type?: 'personal' | 'organization';
  b2b_channel?: B2BChannelType | null;
}): string {
  const groupLabel = CHANNEL_GROUP_LABEL[resolveChannelGroup(order.channel)];
  if (order.user_type !== 'organization') return groupLabel;
  const b2b = order.b2b_channel ? B2B_CHANNEL_SHORT_LABEL[order.b2b_channel] : undefined;
  return b2b ? `B2B ${b2b} ${groupLabel}` : `B2B ${groupLabel}`;
}

/**
 * TCR-1335: chỉ POS và Online còn đang dùng, các group còn lại bị ẩn khỏi bộ
 * lọc. Nhãn/màu/mapping vẫn giữ đủ 6 group để cột Kênh, tag lọc và export vẫn
 * hiển thị đúng cho đơn cũ thuộc kênh đã ẩn.
 * TCR-2127: POS tách thêm B2B Sỉ/GT/MT POS và B2B POS chưa phân kênh.
 */
export const VISIBLE_CHANNEL_FILTERS: OrderChannelFilter[] = [
  'pos',
  'b2b_si_pos',
  'b2b_gt_pos',
  'b2b_mt_pos',
  'b2b_none_pos',
  'online',
];

export const CHANNEL_GROUP_OPTIONS = VISIBLE_CHANNEL_FILTERS.map((k) => ({
  label: `${k === 'online' ? CHANNEL_GROUP_ICON.online : CHANNEL_GROUP_ICON.pos} ${CHANNEL_FILTER_LABEL[k]}`,
  value: k,
}));

export const ORDER_STATUS_GROUP_OPTIONS: { label: string; value: OrderStatusGroup }[] = [
  { value: OrderStatusGroup.ALL, label: 'Tất cả' },
  // TCR-1963 — tab "Nháp" (đơn state=DRAFT, tự huỷ nếu không gửi xác nhận).
  { value: OrderStatusGroup.DRAFT, label: 'Nháp' },
  { value: OrderStatusGroup.AWAITING_CONFIRMATION, label: 'Chờ xác nhận' },
  { value: OrderStatusGroup.NEW, label: 'Mới' },
  { value: OrderStatusGroup.AWAITING_PACKING, label: 'Chờ đóng gói' },
  { value: OrderStatusGroup.PACKING, label: 'Đang đóng gói' },
  { value: OrderStatusGroup.AWAITING_HANDOVER, label: 'Chờ gửi hàng' },
  { value: OrderStatusGroup.HANDOVER, label: 'Đã gửi hàng' },
  { value: OrderStatusGroup.SHIPPING, label: 'Đang giao' },
  { value: OrderStatusGroup.COMPLETED, label: 'Thành công' },
  { value: OrderStatusGroup.RETURNED, label: 'Hoàn' },
  { value: OrderStatusGroup.CANCELLED, label: 'Huỷ' },
];

export const ORDER_STATUS_GROUP_LABEL: Record<OrderStatusGroup, string> = Object.fromEntries(
  ORDER_STATUS_GROUP_OPTIONS.map((option) => [option.value, option.label]),
) as Record<OrderStatusGroup, string>;

export const ORDER_STATUS_GROUP_COLOR: Record<OrderStatusGroup, string> = {
  [OrderStatusGroup.ALL]: 'default',
  [OrderStatusGroup.DRAFT]: 'default',
  [OrderStatusGroup.AWAITING_CONFIRMATION]: 'gold',
  [OrderStatusGroup.NEW]: 'blue',
  [OrderStatusGroup.AWAITING_PACKING]: 'orange',
  [OrderStatusGroup.PACKING]: 'cyan',
  [OrderStatusGroup.AWAITING_HANDOVER]: 'geekblue',
  [OrderStatusGroup.HANDOVER]: 'blue',
  [OrderStatusGroup.SHIPPING]: 'geekblue',
  [OrderStatusGroup.COMPLETED]: 'green',
  [OrderStatusGroup.RETURNED]: 'volcano',
  [OrderStatusGroup.CANCELLED]: 'red',
};

/** antd preset tag color -> hex chữ (shade -8/-9, TCR-2422: ≥ 4.5:1 trên nền trắng), dùng cho label tab dạng chữ-đổi-màu (khác Tag nền màu). Giá trị hex lấy đúng theo antd palette, đồng bộ với ECom/orders/parts/status-style.ts. Export để màn Giao vận dùng chung một bảng, tab hai màn cùng tông. */
export const PRESET_TAG_TEXT_HEX: Record<string, string> = {
  default: '',
  gold: '#874d00',
  blue: '#0958d9',
  orange: '#ad4e00',
  cyan: '#006d75',
  geekblue: '#1d39c4',
  green: '#237804',
  volcano: '#ad2102',
  red: '#cf1322',
};

export function resolveOrderStatusGroupTextColor(group: OrderStatusGroup): string {
  return PRESET_TAG_TEXT_HEX[ORDER_STATUS_GROUP_COLOR[group]] ?? '';
}

// ============================================================
// Trạng thái đơn hàng — hiển thị ĐÚNG từng trạng thái, KHÔNG gom nhóm
// ============================================================
//
// Trước đây UI gom nhiều trạng thái vào một nhãn (`PAID | PARTIAL_PAID |
// DELIVERING` → "Đang xử lý"), khiến đơn đã thu đủ tiền vẫn hiện "Đang xử lý" —
// một nhãn không hề có trong bảng trạng thái nghiệp vụ của TCR-docs. Nhãn giờ
// lấy thẳng từ `getOrderStateText` của @tcr-erp/shared (source of truth chung
// với BE); ở đây chỉ còn phần màu sắc, vốn là chuyện của riêng UI.

// Đặt tên riêng `LEGACY_*` vì `parts/constants.ts` có MỘT export
// `ORDER_STATE_COLOR` khác (dưới, cạnh `ORDER_STATE_LABEL`) do TCR-1088 thêm
// cho `columns.tsx` — 2 export trùng tên từng làm esbuild fail thẳng (lỗi
// "Multiple exports with the same name"), chặn build TOÀN BỘ module Orders.
// Bảng này bao phủ nhiều state hơn (kể cả state cũ/deprecated) nên giữ lại
// cho `resolveOrderStateColor` (dùng ở `detail.tsx`, có thể gặp state cũ
// trong lịch sử trạng thái) — KHÔNG gộp với bảng của `columns.tsx` vì đó chỉ
// cần đúng state hiện hành cho danh sách.
export const LEGACY_ORDER_STATE_COLOR: Record<string, string> = {
  [OrderState.DRAFT]: 'default',
  [OrderState.PENDING]: 'gold',
  [OrderState.WAIT_PAYMENT]: 'orange',
  [OrderState.PARTIAL_PAID]: 'orange',
  [OrderState.PAID]: 'cyan',
  [OrderState.CONFIRMED]: 'blue',
  [OrderState.DELIVERING]: 'geekblue',
  [OrderState.COMPLETED]: 'green',
  [OrderState.CANCELED]: 'red',
  [OrderState.RETURNED]: 'volcano',
  // Deprecated — vẫn gặp trên dữ liệu cũ.
  [OrderState.CUSTOMER_CARE_CONFIRMED]: 'blue',
  [OrderState.WAITING_INVOICE]: 'purple',
  [OrderState.INVOICE_ISSUED]: 'purple',
  [OrderState.PROCESSING]: 'orange',
  [OrderState.UNPAID]: 'orange',
  [OrderState.WAITING_PICKUP]: 'gold',
  [OrderState.REFUNDED]: 'volcano',
  [OrderState.DELIVER_FAILED]: 'red',
  [OrderState.ACCOUNTING_PENDING]: 'gold',
  [OrderState.ACCOUNTING_SUCCESS]: 'green',
  [OrderState.ACCOUNTING_FAILED]: 'red',
};

export function resolveOrderStateColor(state?: string): string {
  return (state && LEGACY_ORDER_STATE_COLOR[state]) || 'default';
}

/** Nhãn trạng thái đơn — KHÔNG truyền `shipping_state` để nhãn vận đơn không đè lên. */
export function resolveOrderStateLabel(state?: string): string {
  return state ? getOrderStateText(state) : '—';
}

/**
 * Nhãn nguồn hoàn tất đơn — phải khớp `ORDER_FINALIZATION_SOURCE_LABEL_VI` bên
 * tcr-erp-be-tenant/src/modules/order/providers/order-finalization.types.ts
 */
const ORDER_FINALIZATION_SOURCE_LABEL: Record<string, string> = {
  manual: 'thao tác thủ công',
  shipping_manual: 'thao tác vận chuyển thủ công',
  carrier_webhook: 'webhook đơn vị vận chuyển',
  carrier_poll: 'kiểm tra trạng thái đơn vị vận chuyển',
  pos_payment: 'thanh toán tại POS',
  pos_customer_handover: 'giao hàng trực tiếp cho khách tại POS',
  pos_self_pod: 'khách tự xác nhận nhận hàng tại POS',
};

/** Ghi chú hệ thống sinh ra với nội dung cố định. */
const ORDER_HISTORY_NOTE_LABEL: Record<string, string> = {
  'Confirmed via aggregate API': 'Xác nhận đơn hàng qua API tổng hợp',
  'Completed via aggregate API': 'Hoàn thành đơn hàng qua API tổng hợp',
  'Updated via PATCH /orders/:id': 'Cập nhật đơn hàng qua API',
  offplatform_order_packing: 'Đóng gói đơn ngoài sàn',
};

/** Ghi chú hệ thống có chèn biến — dịch bằng cách bắt lại phần biến. */
const ORDER_HISTORY_NOTE_PATTERN: { pattern: RegExp; toVi: (m: RegExpMatchArray) => string }[] = [
  {
    pattern: /^Finalized via (\w+)$/,
    toVi: (m) => `Hoàn tất đơn hàng qua ${ORDER_FINALIZATION_SOURCE_LABEL[m[1]] ?? m[1]}`,
  },
  {
    pattern: /^Shipped via (.+): (.+)$/,
    toVi: (m) => `Gửi hàng qua đơn vị vận chuyển ${m[1]}: mã vận đơn ${m[2]}`,
  },
];

/**
 * Dịch ghi chú "Lịch sử trạng thái đơn hàng" sang tiếng Việt lúc hiển thị (TCR-1178).
 *
 * `note` được ghi cứng vào DB lúc chuyển trạng thái, nên bản ghi tạo trước khi BE
 * dịch vẫn giữ nguyên tiếng Anh. Dịch tại đây phủ được cả dữ liệu cũ lẫn mới.
 *
 * Chuỗi không khớp mẫu nào — kể cả ghi chú người dùng tự nhập, hay chuỗi tiếng Anh
 * mới phát sinh — được trả NGUYÊN VĂN, để chỗ còn sót vẫn lộ ra thay vì bị một
 * nhãn mặc định che mất.
 */
export function resolveOrderHistoryNote(note?: string | null): string {
  if (!note) return '';

  const raw = note.trim();
  if (ORDER_HISTORY_NOTE_LABEL[raw]) return ORDER_HISTORY_NOTE_LABEL[raw];

  for (const { pattern, toVi } of ORDER_HISTORY_NOTE_PATTERN) {
    const matched = raw.match(pattern);
    if (matched) return toVi(matched);
  }

  return note;
}

/** Đơn còn ở giai đoạn đầu, chưa chốt — chưa phát sinh chứng từ kế toán. */
export const EARLY_ORDER_STATES: OrderState[] = [
  OrderState.DRAFT,
  OrderState.PENDING,
];

export function isEarlyOrderState(state?: string): boolean {
  return EARLY_ORDER_STATES.includes(state as OrderState);
}

/** Option lọc: các trạng thái vòng đời đang dùng, mỗi trạng thái một dòng. */
export const ORDER_STATE_OPTIONS = ACTIVE_ORDER_STATES.map((s) => ({
  label: getOrderStateText(s),
  value: s,
}));

/** States accepted by the order update endpoint (full or shipping-only edits). */
export const ORDER_EDITABLE_STATES: OrderState[] = [
  OrderState.DRAFT,
  OrderState.PENDING,
  OrderState.CONFIRMED,
  OrderState.DELIVERING,
];

export function isOrderEditableState(state?: string): boolean {
  return ORDER_EDITABLE_STATES.includes(state as OrderState);
}

// ============================================================
// Payment label
// ============================================================

export const ORDER_PAYMENT_LABEL: Record<string, string> = {
  cash: 'Tiền mặt',
  transfer: 'Chuyển khoản',
  card: 'Thẻ',
  mix: 'Hỗn hợp',
  bank: 'Chuyển khoản',
  vietqr: 'VietQR',
  viettinbank: 'VietinBank',
  in_store: 'Tại cửa hàng',
};

export const ORDER_PAYMENT_STATE_LABEL: Record<string, string> = {
  PENDING: 'Chưa thanh toán',
  UNPAID: 'Chưa thanh toán',
  PARTIAL_PAID: 'Thanh toán 1 phần',
  PAID: 'Đã thanh toán',
  FAILED: 'Thanh toán thất bại',
  EXPIRED: 'Hết hạn',
  REFUNDED: 'Đã hoàn tiền',
};

export const ORDER_PAYMENT_STATE_COLOR: Record<string, string> = {
  PENDING: 'red',
  UNPAID: 'red',
  PARTIAL_PAID: 'orange',
  PAID: 'green',
  FAILED: 'red',
  EXPIRED: 'red',
  REFUNDED: 'purple',
};

export const ORDER_STATE_LABEL: Record<string, string> = {
  [OrderState.DRAFT]: 'Nháp',
  [OrderState.PENDING]: 'Mới',
  [OrderState.WAIT_PAYMENT]: 'Chờ thanh toán',
  [OrderState.CONFIRMED]: 'Đã xác nhận',
  [OrderState.CUSTOMER_CARE_CONFIRMED]: 'Đã chăm sóc',
  [OrderState.PAID]: 'Đã thanh toán',
  [OrderState.PARTIAL_PAID]: 'Thanh toán một phần',
  [OrderState.DELIVERING]: 'Đang giao',
  [OrderState.COMPLETED]: 'Hoàn thành',
  [OrderState.CANCELED]: 'Đã huỷ',
  [OrderState.RETURNED]: 'Đã hoàn',
};

export const ORDER_STATE_COLOR: Record<string, string> = {
  [OrderState.DRAFT]: 'default',
  [OrderState.PENDING]: 'blue',
  [OrderState.WAIT_PAYMENT]: 'orange',
  [OrderState.CONFIRMED]: 'cyan',
  [OrderState.CUSTOMER_CARE_CONFIRMED]: 'cyan',
  [OrderState.PAID]: 'green',
  [OrderState.PARTIAL_PAID]: 'orange',
  [OrderState.DELIVERING]: 'processing',
  [OrderState.COMPLETED]: 'green',
  [OrderState.CANCELED]: 'red',
  [OrderState.RETURNED]: 'volcano',
};
